type GetResponseType =
    | NonNullable<Record<string, unknown> | string | number | boolean>
    | NonNullable<Record<string, unknown> | string | number | boolean>[];

type GetResponse<T extends GetResponseType = Record<string, unknown>> = {
    readonly _type: "GET_RESPONSE";
    readonly items: T[];
};

const getResponseFactory = <
    T extends GetResponseType = Record<string, unknown>,
>(params: {
    items: T[];
}): GetResponse<T> => {
    return {
        _type: "GET_RESPONSE",
        items: params.items,
    };
};

type GetSingleResponse<T extends GetResponseType> = {
    readonly _type: "GET_SINGLE_RESPONSE";
    readonly item: T;
};

const getSingleResponseFactory = <
    T extends GetResponseType = Record<string, unknown>,
>(params: {
    item: T;
}): GetSingleResponse<T> => {
    return {
        _type: "GET_SINGLE_RESPONSE",
        item: params.item,
    };
};

type GetDoubleResponse<
    T1 extends GetResponseType = Record<string, unknown>,
    T2 extends GetResponseType = Record<string, unknown>,
> = {
    readonly _type: "GET_DOUBLE_RESPONSE";
    readonly data: [T1, T2];
};

const getDoubleResponseFactory = <
    T1 extends GetResponseType = Record<string, unknown>,
    T2 extends GetResponseType = Record<string, unknown>,
>(params: {
    data: [T1, T2];
}): GetDoubleResponse<T1, T2> => {
    return {
        _type: "GET_DOUBLE_RESPONSE",
        data: params.data,
    };
};

type GetInfiniteResponse<
    T extends GetResponseType = Record<string, unknown>,
    Totals extends Record<string, unknown> = Record<string, unknown>,
> = {
    readonly _type: "GET_INFINITE_RESPONSE";
    readonly items: T[];
    readonly nextCursor: string | undefined;
    readonly totalCount?: number;
    readonly totals?: Totals;
};

const getInfiniteResponseFactory = <
    T extends GetResponseType = Record<string, unknown>,
    Totals extends Record<string, unknown> = Record<string, unknown>,
>(params: {
    items: T[];
    nextCursor: string | undefined;
    totalCount?: number;
    totals?: Totals;
}): GetInfiniteResponse<T> => {
    return {
        _type: "GET_INFINITE_RESPONSE",
        items: params.items,
        nextCursor: params.nextCursor,
        totalCount: params.totalCount,
        totals: params.totals,
    };
};

export {
    getDoubleResponseFactory,
    getInfiniteResponseFactory,
    getResponseFactory,
    getSingleResponseFactory,
};
export type {
    GetDoubleResponse,
    GetInfiniteResponse,
    GetResponse,
    GetSingleResponse,
};
